import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import './AllLiveSessions.css';

const AllLiveSessions: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', name: 'All', icon: '📚' },
    { id: 'math', name: 'Math', icon: '🔢' },
    { id: 'english', name: 'English', icon: '🇬🇧' },
    { id: 'science', name: 'Science', icon: '🔬' },
    { id: 'history', name: 'History', icon: '📜' }
  ];

  // Online Courses (Live Courses) - not individual sessions
  const courses = [
    { 
      id: 1, 
      title: 'Advanced Mathematics Live Course', 
      instructor: 'Dr. Smith', 
      price: 99, 
      originalPrice: 149,
      rating: 4.8, 
      students: 1200, 
      sessions: 24,
      category: 'math', 
      platform: 'BBB',
      image: '📚' 
    },
    { 
      id: 2, 
      title: 'English Conversation Course', 
      instructor: 'Ms. Johnson', 
      price: 79, 
      originalPrice: 119,
      rating: 4.9, 
      students: 850, 
      sessions: 18,
      category: 'english', 
      platform: 'Google Meet',
      image: '📚' 
    },
    { 
      id: 3, 
      title: 'Physics Live Sessions', 
      instructor: 'Dr. Williams', 
      price: 89, 
      originalPrice: 129,
      rating: 4.7, 
      students: 1500, 
      sessions: 30,
      category: 'science', 
      platform: 'BBB',
      image: '📚' 
    },
    { 
      id: 4, 
      title: 'Spanish for Beginners Live', 
      instructor: 'Ms. Garcia', 
      price: 59, 
      originalPrice: 99,
      rating: 4.6, 
      students: 650, 
      sessions: 20,
      category: 'english', 
      platform: 'Google Meet',
      image: '📚' 
    },
    { 
      id: 5, 
      title: 'Chemistry Fundamentals Live', 
      instructor: 'Dr. Brown', 
      price: 95, 
      originalPrice: 139,
      rating: 4.8, 
      students: 1100, 
      sessions: 25,
      category: 'science', 
      platform: 'BBB',
      image: '📚' 
    },
    { 
      id: 6, 
      title: 'World History Live Course', 
      instructor: 'Prof. Davis', 
      price: 69, 
      originalPrice: 109,
      rating: 4.7, 
      students: 920, 
      sessions: 22,
      category: 'history', 
      platform: 'Google Meet',
      image: '📚' 
    }
  ];

  const filteredCourses = courses.filter(course => {
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.instructor.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const sortedCourses = [...filteredCourses].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      default:
        return b.students - a.students;
    }
  });

  return (
    <div className="all-live-sessions-page">
      <header className="all-live-sessions-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1>All Online Courses</h1>
        <button className="filter-button">🔍</button>
      </header>

      <div className="all-live-sessions-content">
        {/* Search Bar */}
        <div className="search-bar-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-large"
          />
        </div>

        {/* Categories Filter */}
        <div className="categories-filter">
          <div className="categories-scroll">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-filter-button ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className="category-icon-small">{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sort Options */}
        <div className="sort-options">
          <span className="sort-label">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="popular">Most Popular</option>
            <option value="rating">Highest Rated</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
        </div>

        {/* Results Count */}
        <div className="results-count">
          {sortedCourses.length} course{sortedCourses.length !== 1 ? 's' : ''} found
        </div>

        {/* Courses Grid */}
        <div className="courses-grid">
          {sortedCourses.map((course) => (
            <div
              key={course.id}
              className="course-card-grid"
              onClick={() => navigate(`/online-course/${course.id}`)}
            >
              <div className="course-image-grid">{course.image}</div>
              <div className="course-info-grid">
                <div className="course-platform-badge">{course.platform}</div>
                <h3 className="course-title-grid">{course.title}</h3>
                <p className="course-instructor-grid">{course.instructor}</p>
                <div className="course-meta-grid">
                  <span className="course-rating-grid">⭐ {course.rating}</span>
                  <span className="course-students-grid">👥 {course.students}</span>
                  <span className="course-sessions-grid">🔴 {course.sessions} sessions</span>
                </div>
                <div className="course-price-container-grid">
                  {course.originalPrice && (
                    <span className="course-original-price-grid">${course.originalPrice}</span>
                  )}
                  <span className="course-price-grid">${course.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default AllLiveSessions;
